import { z } from "zod";
export const SyncTargetKindSchema = z.enum(["obsidian"]);
export const SyncTargetStatusSchema = z.enum([
    "draft",
    "pending",
    "syncing",
    "synced",
    "failed",
    "disabled",
]);
export const SpeakingUsageSchema = z.object({
    casual: z.string().min(1),
    neutral: z.string().min(1),
    formal: z.string().min(1),
});
export const ChunkTypeSchema = z.enum([
    "collocation",
    "phrasal_verb",
    "fixed_expression",
    "idiom",
    "sentence_pattern",
]);
export const CefrLevelSchema = z.enum(["A1", "A2", "B1", "B2", "C1", "C2"]);
export const TopicSchema = z.enum([
    // work & study
    "work_career",
    "business",
    "technology",
    "science",
    "education",
    "communication",
    // daily life
    "daily_life",
    "home",
    "family",
    "relationships",
    "food",
    "shopping",
    "health",
    "sports",
    "travel",
    "transportation",
    "weather",
    "fashion",
    // people & mind
    "emotion",
    "personality",
    "appearance",
    // society & culture
    "society_culture",
    "art_music",
    "entertainment",
    "media",
    "politics",
    "law",
    "finance",
    "nature",
    "environment",
    "abstract",
    "other",
]);
export const FrequencySchema = z.enum(["common", "uncommon", "rare"]);
export const ChunkSchema = z.object({
    text: z.string().min(1),
    chunk_type: ChunkTypeSchema.default("collocation"),
    cefr_level: CefrLevelSchema.optional(),
    example: z.string().min(1),
});
export const VocabDataSchema = z.object({
    schema_version: z.literal(1).default(1),
    ipa: z.string().min(1),
    type: z.string().min(1),
    meaning_simple_en: z.string().min(1),
    synonyms: z.array(z.string().min(1)).default([]),
    antonyms: z.array(z.string().min(1)).default([]),
    core_idea: z.string().min(1),
    examples: z.array(z.string().min(1)).min(2).max(3),
    chunks: z.array(ChunkSchema).min(3).max(5),
    speaking_usage: SpeakingUsageSchema,
    common_mistakes: z.array(z.string()).max(4),
    memory_hook: z.string().min(1),
    vn_phrase_translation: z.string().min(1),
    topics: z.array(TopicSchema).min(1).max(3),
    frequency: FrequencySchema,
    cefr_level: CefrLevelSchema,
    ai_model: z.string().min(1),
    ai_prompt_version: z.string().min(1),
    generated_at: z.string().datetime(),
    audio_url: z.string().url().optional(),
});
export const VocabEntrySchema = z.object({
    id: z.string().uuid(),
    user_id: z.string().uuid(),
    word: z.string().min(1),
    part_of_speech: z.string().nullable(),
    meaning: z.string().min(1),
    my_sentence: z.string(),
    vietnamese: z.string().nullable(),
    source_url: z.string().url().nullable(),
    tags: z.array(z.string()),
    data: VocabDataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const ObsidianTargetPayloadSchema = z.object({
    path: z.string().min(1),
    markdown: z.string().min(1),
    tags: z.array(z.string()),
});
export const SyncTargetPayloadSchema = ObsidianTargetPayloadSchema;
export const SyncTargetSchema = z.object({
    id: z.string().uuid(),
    entry_id: z.string().uuid(),
    kind: SyncTargetKindSchema,
    enabled: z.boolean(),
    status: SyncTargetStatusSchema,
    payload: SyncTargetPayloadSchema,
    payload_user_edited: z.boolean(),
    target_id: z.string().nullable(),
    target_url: z.string().nullable(),
    last_attempt_at: z.string().datetime().nullable(),
    last_error: z.string().nullable(),
    retry_count: z.number().int().nonnegative(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateVocabRequestSchema = z.object({
    word: z.string().min(1).max(80),
    my_sentence: z.string().max(2000).default(""),
    vietnamese: z.string().max(200).nullable().optional(),
    source_url: z.string().url().max(500).nullable().optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    data: VocabDataSchema,
});
export const VocabEntryWithTargetsSchema = VocabEntrySchema.extend({
    targets: z.array(SyncTargetSchema),
});
export const VocabListQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    type: z.string().trim().max(40).optional(),
    tag: z.string().trim().max(40).optional(),
    topic: TopicSchema.optional(),
    frequency: FrequencySchema.optional(),
    sync_status: SyncTargetStatusSchema.optional(),
    created_from: z.string().datetime().optional(),
    created_to: z.string().datetime().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const VocabListResponseSchema = z.object({
    items: z.array(VocabEntryWithTargetsSchema),
    next_cursor: z.string().nullable(),
    has_more: z.boolean(),
});
export const UpdateVocabRequestSchema = z.object({
    entry: z
        .object({
        word: z.string().min(1).max(80).optional(),
        my_sentence: z.string().min(1).max(2000).optional(),
        vietnamese: z.string().max(200).nullable().optional(),
        tags: z.array(z.string().min(1).max(40)).max(20).optional(),
        meaning_simple_en: z.string().min(1).optional(),
        type: z.string().min(1).optional(),
        ipa: z.string().min(1).optional(),
        synonyms: z.array(z.string().min(1)).optional(),
        antonyms: z.array(z.string().min(1)).optional(),
        core_idea: z.string().min(1).optional(),
        examples: z.array(z.string().min(1)).max(3).optional(),
        chunks: z.array(ChunkSchema).max(5).optional(),
        speaking_usage: SpeakingUsageSchema.optional(),
        common_mistakes: z.array(z.string()).max(4).optional(),
        memory_hook: z.string().min(1).optional(),
        vn_phrase_translation: z.string().min(1).optional(),
        topics: z.array(TopicSchema).min(1).max(3).optional(),
        frequency: FrequencySchema.optional(),
        cefr_level: CefrLevelSchema.optional(),
    })
        .refine((value) => Object.keys(value).length > 0, {
        message: "At least one field must be provided",
    }),
});
export const SyncVocabRequestSchema = z.object({
    failed_only: z.boolean().default(true),
});
export const VocabAudioQuerySchema = z.object({
    word: z.string().trim().min(1).max(80),
});
export const VocabAudioResponseSchema = z.object({
    word: z.string(),
    ipa: z.string().nullable(),
    audio_url: z.string().url().nullable(),
});
//# sourceMappingURL=vocab.js.map